import { createDefaultBroadcastCondition } from '../../../utils/broadcastContents';
import { patchDelayConditionAt, removeAt } from '../logic';

import type { BroadcastConditionsEditorProps } from '../types';
import { BroadcastConditionRow } from './BroadcastConditionRow';

// ===========================================================================
// 任意テキスト放送 (free_text) の「放送条件」編集 UI
// ===========================================================================
//
// `FreeTextProperties.conditions?: BroadcastCondition[]` を編集する。未指定/空配列は
// 「無条件放送」、複数指定は AND 判定 (すべて満たしたときのみ放送) となる。
//
// 1 条件は `condition_type` を discriminant とする判別可能ユニオンで、2 種別を扱う:
//
//   - `connecting_line_delay` (接続線遅延):
//     「(接続線 route_id, 判定モード, モード別入力, 遅延分数, 演算子 ≧/≦)」で表現し、
//     接続線遅延コンテンツ (`ConnectingLineDelayFields`) と同じ 3 モードを持つ。
//       * `whole_line`       : 接続線全体の最大遅延
//       * `section_scheduled`: 区間 (start/end_stop_id) + 定刻在線便の遅延
//       * `transfer_aware`   : 乗換駅・乗換時間を考慮した「乗り換えようとしている便」の遅延
//   - `arrival_track` (停車番線):
//     自列車の停車番線 = GTFS 子 stop_id (例 `"南浦和:上中"`) が
//     停車駅 (`stop_times` 全行) の集合に含まれるかで判定する
//     (終着駅でも途中停車駅でも成立 / 通過駅は不成立)。
//     入力は「停車駅 → 停車番線」の 2 段ドロップダウン。
//
// 種別の切替は行 (`BroadcastConditionRow`) 側で **条件オブジェクトを丸ごと生成** して
// `onReplace` で返す。patch でマージすると他種別のフィールドが DB / JSON に残るため。

export function BroadcastConditionsEditor({
  conditions,
  routes,
  routeStops,
  gtfsStops = [],
  parentRouteId,
  onChange,
}: BroadcastConditionsEditorProps): React.JSX.Element {
  const handleAddCondition = (): void => {
    onChange([...conditions, createDefaultBroadcastCondition()]);
  };

  const handleRemoveCondition = (index: number): void => {
    onChange(removeAt(conditions, index));
  };

  return (
    <fieldset
      className="broadcast-conditions-editor"
      style={{ border: '1px solid #ccc', padding: 8, margin: '8px 0' }}
      data-testid="broadcast-conditions-editor"
    >
      <legend style={{ padding: '0 4px' }}>放送条件 (任意)</legend>
      <p style={{ fontSize: '0.85em', color: '#555', margin: '4px 0 8px' }}>
        接続線遅延 / 停車番線を条件に、任意テキストの放送可否を制御できます。 条件を{' '}
        <strong>1件も追加しなければ「無条件で放送」</strong>、
        複数追加した場合はすべての条件を満たしたとき (AND) にのみ放送します。
      </p>

      {conditions.length === 0 ? (
        <p style={{ margin: '4px 0', color: '#666' }}>
          条件は設定されていません (無条件で放送されます)。
        </p>
      ) : (
        <ol className="broadcast-conditions-list" style={{ paddingLeft: 20, margin: 0 }}>
          {conditions.map((cond, index) => (
            <li
              key={index}
              className="broadcast-condition-row"
              data-testid={`broadcast-condition-row-${index}`}
              style={{ margin: '8px 0', padding: 8, background: '#fafafa', borderRadius: 4 }}
            >
              <BroadcastConditionRow
                condition={cond}
                routes={routes}
                routeStops={routeStops}
                gtfsStops={gtfsStops}
                parentRouteId={parentRouteId}
                // patch は **接続線遅延条件の部分更新専用** の経路。
                //
                // 以前は `patchCondition(conditions as ConnectingLineDelayCondition[], ...)
                // as BroadcastCondition[]` と配列全体を二重キャストしていたが、
                // 混在配列を片方のメンバに偽装する危険なキャストだった。
                // ジェネリック化済みの `patchCondition` は「同一メンバ内の部分更新」だけを
                // 許可するので、**対象行 1 件に絞り込んでから** 適用すればキャストは不要。
                // 停車番線条件の行はこの経路を使わず、完成済みオブジェクトを返す
                // `onReplace` に寄せている。
                onChange={(patch) => onChange(patchDelayConditionAt(conditions, index, patch))}
                onReplace={(next) => onChange(conditions.map((c, i) => (i === index ? next : c)))}
                onRemove={() => handleRemoveCondition(index)}
              />
            </li>
          ))}
        </ol>
      )}

      <div style={{ marginTop: 8 }}>
        <button type="button" onClick={handleAddCondition} data-testid="broadcast-condition-add">
          ＋ 条件を追加
        </button>
      </div>
    </fieldset>
  );
}
